import { useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/Button";
import { Mark } from "@/components/brand/Logo";
import { ViewfinderFrame } from "@/components/brand/ViewfinderFrame";
import { useDocumentMeta } from "@/lib/useDocumentMeta";

/**
 * Marketing landing page at `/` — beta-ready revision.
 *
 * Restructures the M12a landing around first-club adoption. Changes vs.
 * the prior version, all grounded in the brand book (lowercase, viewfinder
 * + O · X · cyan-dotted-line vocabulary, cyan-underline accent — never cyan
 * body text on white):
 *
 *   1. Hero telestration restored to a COMPLETE play — two O's circled, the
 *      X to beat, a cyan dotted pass line, and a solid white off-ball run
 *      arrow. (The prior `Telestration` rendered only two static ellipses.)
 *   2. Funnel collapses to one primary CTA — "set up your team" — instead of
 *      two equal-weight CTAs split by the internal word "hub".
 *   3. New trust/privacy section: an interactive "who sees what" explorer.
 *      Youth-footage privacy is the #1 club-director / guardian objection
 *      (see docs/PRIVACY.md).
 *   4. Beta framing ("now onboarding founding clubs") + a validated
 *      founding-club request form as the closing CTA.
 *   5. Outcome-first feature copy, a how-it-works bridge, a pilot proof
 *      quote, and a 5-question FAQ that removes adoption friction.
 *
 * Entrance animation note: `.reveal` keeps a resting `opacity: 1` and only
 * animates `transform`, so a frozen first frame (hidden tab, print/PDF,
 * reduced-motion) still shows fully. Do not move opacity into the keyframe.
 */
export function LandingPage() {
  useDocumentMeta({
    title: "tktic — coach feedback, delivered",
    description:
      "Raw match footage turned into focused feedback that reaches the player — by Tuesday. Film, notes, and live broadcasts in one private place, built for youth football clubs.",
    ogType: "website",
  });

  return (
    <main data-testid="landing-page" className="bg-canvas text-ink">
      <BetaStrip />
      <Hero />
      <Features />
      <HowItWorks />
      <Proof />
      <Privacy />
      <Faq />
      <Join />
      <FooterStrip />
    </main>
  );
}

// ---------------------------------------------------------------------------
// Beta strip
// ---------------------------------------------------------------------------

function BetaStrip() {
  return (
    <div className="bg-ink text-ink-inverse">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-3 px-6 py-2.5">
        <span
          aria-hidden="true"
          className="size-2 animate-pulse rounded-full bg-accent ring-4 ring-accent/25"
        />
        <span className="font-mono text-[11px] uppercase tracking-mono">
          now onboarding founding clubs
          <span className="hidden md:inline"> · central ohio · limited spots</span>
        </span>
        <a
          href="#join"
          className="font-display text-xs font-semibold lowercase text-accent"
        >
          claim yours ›
        </a>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

function Hero() {
  return (
    <section
      data-testid="landing-hero"
      className="mx-auto flex max-w-6xl flex-col gap-12 px-6 pt-16 pb-20 lg:flex-row lg:items-center lg:gap-14 lg:pt-24 lg:pb-28"
    >
      <div className="reveal flex flex-1 flex-col gap-6">
        <span className="inline-flex w-fit items-center gap-2 rounded-full border border-rule bg-field px-3 py-1 font-display text-xs font-medium lowercase text-ink-secondary">
          built by a coach, for the touchline
        </span>
        <h1
          data-testid="landing-hero-headline"
          className="font-display text-4xl font-bold lowercase leading-[0.98] tracking-hero text-ink sm:text-5xl lg:text-[5.5rem]"
        >
          coach feedback,
          <br />
          <AccentLine>delivered.</AccentLine>
        </h1>
        <p
          data-testid="landing-hero-subhead"
          className="max-w-xl font-body text-lg leading-snug lowercase text-ink-secondary sm:text-xl"
        >
          raw match footage, turned into focused feedback that actually reaches
          the player — by tuesday, not someday. one place for film, notes, and
          live broadcasts.
        </p>
        <div
          data-testid="landing-hero-ctas"
          className="flex flex-col gap-3 sm:flex-row sm:items-center"
        >
          <Button asChild size="lg" variant="primary">
            <Link
              to="/login"
              search={{ next: "/hub" }}
              data-testid="landing-cta-hub"
              className="lowercase"
            >
              set up your team
              <span aria-hidden="true" className="ml-1 tracking-tighter">
                ›››
              </span>
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <a href="#how" data-testid="landing-cta-how" className="lowercase">
              see how it works
            </a>
          </Button>
        </div>
        <ul className="mt-2 flex flex-wrap gap-x-7 gap-y-2">
          {["private by default", "no app to install", "~15 min setup"].map(
            (t) => (
              <li
                key={t}
                className="flex items-center gap-2 font-display text-sm font-medium lowercase text-ink-muted"
              >
                <CheckIcon className="size-4 text-accent" />
                {t}
              </li>
            ),
          )}
        </ul>
      </div>
      <div className="reveal flex flex-1 items-center justify-center">
        <HeroViewfinder />
      </div>
    </section>
  );
}

/** Hero viewfinder: match frame + the complete coaching markup, framed by
 * the brand brackets. Falls back to the wordmark Mark if `/hero-match.jpg`
 * is missing. */
function HeroViewfinder() {
  const [imageOk, setImageOk] = useState(true);
  return (
    <div className="aspect-[16/10] w-full max-w-[520px] rounded border border-rule bg-surface p-4 shadow-sm">
      <div className="relative h-full overflow-hidden rounded bg-navy-900">
        {imageOk ? (
          <img
            src="/hero-match.jpg"
            alt=""
            className="absolute inset-0 size-full object-cover"
            onError={() => setImageOk(false)}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-surface">
            <Mark size={120} aria-hidden="true" />
          </div>
        )}
        {imageOk && <Telestration />}
        <ViewfinderFrame
          label="PLAY 04 · 23:14"
          color="#ffffff"
          style={{
            position: "absolute",
            inset: 0,
            filter: "drop-shadow(0 0 2px rgba(0,0,0,0.55))",
          }}
        />
      </div>
    </div>
  );
}

/** Complete coach telestration over the hero frame.
 *
 * Two O's circled in cyan (ball-carrier + receiver), an X for the defender
 * to beat, a cyan dotted pass line carrier→receiver, and a solid white
 * curved arrow tracing the receiver's off-ball run. Coords tuned to the
 * `/hero-match.jpg` framing in a 160×100 viewBox (16:10 viewfinder). */
function Telestration() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 160 100"
      preserveAspectRatio="none"
      className="pointer-events-none absolute inset-0 size-full"
      style={{ filter: "drop-shadow(0 0 1.2px rgba(0,0,0,0.7))" }}
    >
      <defs>
        <marker
          id="tktic-run-arrow"
          viewBox="0 0 10 10"
          refX="7"
          refY="5"
          markerWidth="5"
          markerHeight="5"
          orient="auto-start-reverse"
        >
          <path d="M0 0L10 5L0 10z" fill="#ffffff" />
        </marker>
      </defs>

      {/* O — ball-carrier */}
      <circle
        cx="40"
        cy="62"
        r="9"
        fill="none"
        stroke="var(--accent)"
        strokeWidth="2"
      />
      {/* O — receiver */}
      <circle
        cx="116"
        cy="34"
        r="9"
        fill="none"
        stroke="var(--accent)"
        strokeWidth="2"
      />
      {/* X — defender to beat */}
      <g stroke="var(--flag)" strokeWidth="2" strokeLinecap="round">
        <line x1="82" y1="40" x2="94" y2="52" />
        <line x1="94" y1="40" x2="82" y2="52" />
      </g>
      {/* cyan dotted pass line */}
      <path
        d="M49 58 Q 84 40 108 36"
        fill="none"
        stroke="var(--accent)"
        strokeWidth="2"
        strokeDasharray="2 4"
        strokeLinecap="round"
      />
      {/* solid white off-ball run */}
      <path
        d="M104 50 Q 110 40 114 26"
        fill="none"
        stroke="#ffffff"
        strokeWidth="2"
        strokeLinecap="round"
        markerEnd="url(#tktic-run-arrow)"
      />
    </svg>
  );
}

/** Render an inline span with a thin cyan rule under it — the brand's
 * accent treatment for headline emphasis that doesn't fail
 * cyan-on-white contrast. */
function AccentLine({ children }: { children: React.ReactNode }) {
  return (
    <span
      data-testid="landing-hero-accent"
      className="border-b-[6px] border-accent text-ink"
    >
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Features — outcome-first
// ---------------------------------------------------------------------------

const FEATURES: Array<{
  testid: string;
  title: string;
  body: string;
  icon: (props: { className?: string }) => JSX.Element;
}> = [
  {
    testid: "landing-feature-film",
    title: "no more group-chat video links",
    body: "every match in one place, sorted by team and season. upload or stream once — coaches, players, and families all find it.",
    icon: ({ className }) => (
      <svg
        className={className}
        viewBox="0 0 32 32"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="5" y="7" width="22" height="18" rx="2" />
        <path d="M5 12h22M5 20h22M10 7v18M22 7v18" />
      </svg>
    ),
  },
  {
    testid: "landing-feature-feedback",
    title: "the player actually sees it",
    body: "mark the moment, say the lesson, send to one player or the whole squad. notes, clips, and goals live right on the video.",
    icon: ({ className }) => (
      <svg
        className={className}
        viewBox="0 0 32 32"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6 9h14a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3h-6l-6 5v-5H6a3 3 0 0 1-3-3v-6a3 3 0 0 1 3-3Z" />
        <circle cx="11" cy="15" r="1.2" fill="currentColor" />
        <circle cx="16" cy="15" r="1.2" fill="currentColor" />
        <circle cx="21" cy="15" r="1.2" fill="currentColor" />
      </svg>
    ),
  },
  {
    testid: "landing-feature-live",
    title: "families never miss a match",
    body: "broadcast straight from the sideline. anyone with the team link watches live, then comes back for the replay.",
    icon: ({ className }) => (
      <svg
        className={className}
        viewBox="0 0 32 32"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="16" cy="16" r="3" fill="currentColor" />
        <path d="M9 9a10 10 0 0 0 0 14M23 9a10 10 0 0 1 0 14" />
        <path d="M5.5 5.5a14 14 0 0 0 0 21M26.5 5.5a14 14 0 0 1 0 21" />
      </svg>
    ),
  },
];

function Features() {
  return (
    <section
      data-testid="landing-features"
      aria-labelledby="landing-features-heading"
      className="border-t border-rule bg-elevated"
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-10 px-6 py-16 lg:py-20">
        <div className="flex flex-col gap-1">
          <span className="font-mono text-[11px] uppercase tracking-mono text-ink-muted">
            / what tktic does
          </span>
          <h2
            id="landing-features-heading"
            className="font-display text-2xl lowercase tracking-display text-ink sm:text-3xl"
          >
            three jobs, one room.
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {FEATURES.map((f) => (
            <article
              key={f.testid}
              data-testid={f.testid}
              className="flex flex-col gap-3 rounded border border-rule bg-surface p-6"
            >
              <f.icon className="size-7 text-ink" />
              <h3 className="font-display text-lg lowercase leading-snug tracking-tight text-ink">
                {f.title}
              </h3>
              <p className="font-body text-sm lowercase leading-snug text-ink-secondary">
                {f.body}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// How it works
// ---------------------------------------------------------------------------

const STEPS = [
  {
    n: "01",
    title: "capture the match",
    body: "stream from a phone on the sideline, or upload the file after. it lands on your team page automatically.",
  },
  {
    n: "02",
    title: "mark the moment",
    body: "scrub to the play, draw the run, say the lesson. tag the player it's for and set who can see it.",
  },
  {
    n: "03",
    title: "it reaches the player",
    body: "they open a link — no app — watch the clip, and tap back: got it, need help, or i tried this.",
  },
];

function HowItWorks() {
  return (
    <section
      id="how"
      data-testid="landing-how"
      className="mx-auto flex max-w-6xl flex-col gap-10 px-6 py-16 lg:py-20"
    >
      <div className="flex flex-col gap-1">
        <span className="font-mono text-[11px] uppercase tracking-mono text-ink-muted">
          / how it works
        </span>
        <h2 className="font-display text-2xl lowercase tracking-display text-ink sm:text-3xl">
          kickoff to car ride home.
        </h2>
      </div>
      <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {STEPS.map((s) => (
          <div key={s.n} className="flex flex-col gap-2">
            <span className="font-display text-4xl font-bold tracking-hero text-accent">
              {s.n}
            </span>
            <h3 className="font-display text-lg lowercase tracking-tight text-ink">
              {s.title}
            </h3>
            <p className="max-w-xs font-body text-sm lowercase leading-snug text-ink-secondary">
              {s.body}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Proof
// ---------------------------------------------------------------------------

function Proof() {
  return (
    <section
      data-testid="landing-proof"
      className="border-y border-rule bg-elevated"
    >
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-10 px-6 py-14">
        <blockquote className="min-w-[320px] flex-1">
          <p className="font-display text-2xl lowercase leading-snug tracking-tight text-ink sm:text-3xl">
            “the clip reached my player before they’d even gotten home from the
            game. he watched it three times.”
          </p>
          <footer className="mt-4 font-mono text-[11px] uppercase tracking-mono text-ink-muted">
            — guardian · founding club pilot
          </footer>
        </blockquote>
        <dl className="flex gap-8">
          <div>
            <dt className="sr-only">match to feedback</dt>
            <dd className="font-display text-3xl font-bold tracking-tight text-ink">
              &lt; 4 hrs
            </dd>
            <span className="font-mono text-[11px] uppercase tracking-mono text-ink-muted">
              match to feedback
            </span>
          </div>
          <div>
            <dt className="sr-only">access</dt>
            <dd className="font-display text-3xl font-bold tracking-tight text-ink">
              1 link
            </dd>
            <span className="font-mono text-[11px] uppercase tracking-mono text-ink-muted">
              no app, ever
            </span>
          </div>
        </dl>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Privacy explorer — interactive "who sees what"
// ---------------------------------------------------------------------------

type PrivacyRole = "player" | "guardian" | "coach" | "public";

const PRIVACY: Record<
  PrivacyRole,
  { label: string; sees: string[]; cant: string[] }
> = {
  player: {
    label: "a player",
    sees: [
      "clips & notes their coach sent them",
      "their own goals and progress",
      "their team's matches",
    ],
    cant: ["other players' private feedback", "anything from other clubs"],
  },
  guardian: {
    label: "a guardian",
    sees: [
      "everything sent to their child",
      "their child's team matches, live + replay",
    ],
    cant: ["other families' feedback", "coach-only tactical notes"],
  },
  coach: {
    label: "the coach",
    sees: [
      "the full match library for their team",
      "every note, clip and goal they've created",
      "who has watched what",
    ],
    cant: ["other teams' footage in the club", "teams they don't coach"],
  },
  public: {
    label: "the public",
    sees: [
      "only what a club explicitly makes public",
      "a public team's match stream, if enabled",
    ],
    cant: ["any player feedback — ever", "private or unlisted teams"],
  },
};

function Privacy() {
  const [role, setRole] = useState<PrivacyRole>("player");
  const r = PRIVACY[role];
  return (
    <section
      id="privacy"
      data-testid="landing-privacy"
      className="mx-auto flex max-w-6xl flex-col gap-8 px-6 py-16 lg:py-20"
    >
      <div className="flex flex-col gap-2">
        <span className="font-mono text-[11px] uppercase tracking-mono text-ink-muted">
          / privacy first
        </span>
        <h2 className="font-display text-2xl lowercase tracking-display text-ink sm:text-3xl">
          it's kids' footage. we treat it that way.
        </h2>
        <p className="max-w-xl font-body text-sm lowercase leading-snug text-ink-secondary">
          nothing is public unless your club deliberately makes it so. see
          exactly what each person can — and can't — reach.
        </p>
      </div>

      <div className="rounded border border-rule bg-surface p-6">
        <div
          role="tablist"
          aria-label="role"
          className="mb-6 inline-flex flex-wrap gap-1 rounded border border-rule bg-field p-[3px]"
        >
          {(Object.keys(PRIVACY) as PrivacyRole[]).map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={role === k}
              data-testid={`landing-privacy-tab-${k}`}
              onClick={() => setRole(k)}
              className="rounded px-3 py-1.5 font-display text-sm font-medium lowercase text-ink-muted transition-colors duration-fast ease-out hover:text-ink aria-selected:bg-surface aria-selected:text-ink aria-selected:shadow-sm"
            >
              {PRIVACY[k].label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <div className="mb-3 flex items-center gap-2">
              <span className="inline-flex size-[18px] items-center justify-center rounded-full bg-good/15 text-good">
                <CheckIcon className="size-3" />
              </span>
              <span className="font-display text-[11px] font-medium uppercase tracking-mono text-ink-muted">
                {r.label} sees
              </span>
            </div>
            <ul className="flex flex-col gap-2">
              {r.sees.map((s) => (
                <li
                  key={s}
                  className="flex gap-2 font-body text-sm lowercase leading-snug text-ink-secondary"
                >
                  <CheckIcon className="mt-0.5 size-3.5 shrink-0 text-good" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="mb-3 flex items-center gap-2">
              <span className="inline-flex size-[18px] items-center justify-center rounded-full bg-destructive/15 text-destructive">
                <LockIcon className="size-2.5" />
              </span>
              <span className="font-display text-[11px] font-medium uppercase tracking-mono text-ink-muted">
                never sees
              </span>
            </div>
            <ul className="flex flex-col gap-2">
              {r.cant.map((s) => (
                <li
                  key={s}
                  className="flex gap-2 font-body text-sm lowercase leading-snug text-ink-muted"
                >
                  <XIcon className="mt-0.5 size-3.5 shrink-0 text-destructive" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// FAQ
// ---------------------------------------------------------------------------

const FAQ_ITEMS = [
  {
    q: "what does it cost during the beta?",
    a: "Nothing. Founding clubs use tktic free through the beta. After that you'll get founding-club pricing locked in — with plenty of notice before anything changes.",
  },
  {
    q: "who can see my child's footage?",
    a: "Only the people on your team. Feedback is private by default — a player sees what their coach sent them, guardians see their own child's, and nothing is public unless your club deliberately makes a team public. No cross-club access, ever.",
  },
  {
    q: "how long does setup take?",
    a: "About fifteen minutes. You create your team, add players by name or invite link, and upload (or stream) your first match. We'll do it with you on a call for the first club.",
  },
  {
    q: "do families need to download an app?",
    a: "No. Everything works from a link in a browser — watch live, open a clip, reply to a note. One less thing for a parent to install.",
  },
  {
    q: "what happens to our video?",
    a: "It stays yours. Stored privately for your team, never sold, never used to train anything, and you can export or delete it whenever you want.",
  },
];

function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section
      id="faq"
      data-testid="landing-faq"
      className="border-t border-rule bg-elevated"
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-8 px-6 py-16 lg:py-20">
        <div className="flex flex-col gap-1">
          <span className="font-mono text-[11px] uppercase tracking-mono text-ink-muted">
            / before you ask
          </span>
          <h2 className="font-display text-2xl lowercase tracking-display text-ink sm:text-3xl">
            the honest questions.
          </h2>
        </div>
        <div className="flex flex-col gap-2">
          {FAQ_ITEMS.map((it, i) => {
            const isOpen = open === i;
            return (
              <div
                key={it.q}
                className="overflow-hidden rounded border border-rule bg-surface"
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  data-testid={`landing-faq-toggle-${i}`}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="font-display text-base font-semibold lowercase tracking-tight text-ink">
                    {it.q}
                  </span>
                  <ChevronIcon
                    className={`size-[18px] shrink-0 text-ink-muted transition-transform duration-fast ease-out ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <div
                  className="grid transition-all duration-slow ease-out"
                  style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
                >
                  <div className="overflow-hidden">
                    <p className="max-w-2xl px-5 pb-4 font-body text-sm leading-relaxed text-ink-secondary">
                      {it.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Join — founding-club request (validated)
// ---------------------------------------------------------------------------

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function Join() {
  const [club, setClub] = useState("");
  const [email, setEmail] = useState("");
  const [err, setErr] = useState<"" | "club" | "email">("");
  const [done, setDone] = useState(false);
  const liveRef = useRef<HTMLParagraphElement>(null);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!club.trim()) return setErr("club");
    if (!EMAIL_RE.test(email)) return setErr("email");
    setErr("");
    // TODO(beta): POST to the founding-club waitlist endpoint.
    setDone(true);
  }

  return (
    <section
      id="join"
      data-testid="landing-join"
      className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-6 py-16 lg:grid-cols-2 lg:py-24"
    >
      <div className="flex flex-col gap-4">
        <span className="font-mono text-[11px] uppercase tracking-mono text-ink-muted">
          / founding clubs
        </span>
        <h2 className="font-display text-4xl font-bold lowercase leading-none tracking-hero text-ink">
          be the first club{" "}
          <span className="border-b-[6px] border-accent">on tktic.</span>
        </h2>
        <p className="max-w-md font-body text-base lowercase leading-snug text-ink-secondary">
          we're onboarding a handful of clubs by hand this season — free, with
          setup help on a call. you shape what we build next.
        </p>
      </div>

      {done ? (
        <div
          data-testid="landing-join-success"
          className="rounded border border-good/40 bg-surface p-8 text-center"
        >
          <span className="mx-auto mb-3 inline-flex size-11 items-center justify-center rounded-full bg-good/15 text-good">
            <CheckIcon className="size-6" />
          </span>
          <h3 className="font-display text-xl font-bold lowercase tracking-tight text-ink">
            you're on the list, {club.trim().toLowerCase()}.
          </h3>
          <p
            ref={liveRef}
            aria-live="polite"
            className="mt-2 font-body text-sm text-ink-secondary"
          >
            we'll reach out at{" "}
            <span className="font-mono text-ink">{email}</span> to set up your
            first match together.
          </p>
        </div>
      ) : (
        <form
          onSubmit={submit}
          noValidate
          data-testid="landing-join-form"
          className="rounded border border-rule bg-surface p-6"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5">
              <span className="font-display text-[11px] font-medium uppercase tracking-mono text-ink-muted">
                club or team name
              </span>
              <input
                value={club}
                onChange={(e) => {
                  setClub(e.target.value);
                  setErr("");
                }}
                placeholder="e.g. central ohio steel"
                className={`h-10 rounded border bg-elevated px-3 font-body text-sm text-ink outline-none transition-colors focus:border-accent ${
                  err === "club" ? "border-destructive" : "border-rule"
                }`}
              />
              {err === "club" && (
                <span className="text-xs text-destructive">
                  tell us your club's name
                </span>
              )}
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="font-display text-[11px] font-medium uppercase tracking-mono text-ink-muted">
                email
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErr("");
                }}
                placeholder="you@club.com"
                className={`h-10 rounded border bg-elevated px-3 font-body text-sm text-ink outline-none transition-colors focus:border-accent ${
                  err === "email" ? "border-destructive" : "border-rule"
                }`}
              />
              {err === "email" && (
                <span className="text-xs text-destructive">
                  enter a valid email
                </span>
              )}
            </label>
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <span className="font-mono text-[11px] text-ink-muted">
              no card · free through beta · ~15 min setup
            </span>
            <Button type="submit" size="lg" variant="primary">
              <span className="lowercase">request founding access</span>
              <span aria-hidden="true" className="ml-1 tracking-tighter">
                ›››
              </span>
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

function FooterStrip() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="landing-footer"
      className="border-t border-rule bg-elevated"
    >
      <div className="mx-auto flex max-w-6xl flex-col items-start gap-3 px-6 py-8 font-mono text-xs uppercase tracking-mono text-ink-muted sm:flex-row sm:items-center sm:justify-between">
        <span data-testid="landing-footer-brand">
          tktic · {year} · built for the touchline
        </span>
        <nav className="flex gap-5">
          <a href="/privacy" className="lowercase hover:text-ink">
            privacy
          </a>
          <a href="/terms" className="lowercase hover:text-ink">
            terms
          </a>
          <a href="mailto:hello@tktic.app" className="lowercase hover:text-ink">
            contact
          </a>
        </nav>
      </div>
    </footer>
  );
}

// ---------------------------------------------------------------------------
// Inline icons (match the existing inline-SVG convention in this file)
// ---------------------------------------------------------------------------

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
